import { slugify } from '../utils/slug'

export type ServiceCategory = {
  name: string
  slug: string
  icon: string
  blurb: string
  services: { name: string; slug: string }[]
}

const rawCategories: {
  name: string
  icon: string
  blurb: string
  services: string[]
}[] = [
  {
    name: 'Diagnostics & Inspections',
    icon: 'gauge',
    blurb:
      'Testing that finds the actual cause before anything is replaced or quoted.',
    services: [
      'Check Engine Light Diagnostics',
      'Computer & Electrical Diagnostics',
      'No-Start / No-Crank Diagnostics',
      'Starting & Charging System Testing',
      'Vehicle Safety Inspections',
      'Pre-Purchase Vehicle Inspections',
      'General Vehicle Inspections',
      'Fluid Leak Diagnosis',
      'Noise & Vibration Diagnosis',
      'Drivability Diagnostics',
    ],
  },
  {
    name: 'Preventive Maintenance',
    icon: 'droplet',
    blurb:
      'Routine service that keeps your vehicle on schedule and out of the repair bay.',
    services: [
      'Oil & Filter Changes',
      'Factory Scheduled Maintenance',
      'Fluid Inspections & Services',
      'Air Filter Replacement',
      'Cabin Air Filter Replacement',
      'Spark Plug Replacement',
      'Fuel System Maintenance',
      'Belt Replacement',
      'Hose Replacement',
      'Wiper Blade Replacement',
      'Battery Maintenance & Replacement',
    ],
  },
  {
    name: 'Brake Services',
    icon: 'disc',
    blurb:
      'Inspection and repair for how your vehicle stops, from pads to the ABS system.',
    services: [
      'Brake Inspections',
      'Brake Pad Replacement',
      'Brake Rotor Replacement',
      'Brake Caliper Replacement',
      'Brake Hose & Brake Line Repair',
      'Brake Fluid Service',
      'Brake Master Cylinder Replacement',
      'ABS Diagnostics & Repair',
      'Parking Brake Repair',
    ],
  },
  {
    name: 'Steering & Suspension',
    icon: 'crosshair',
    blurb:
      'Repairs for how your vehicle rides, steers, and holds the road.',
    services: [
      'Steering & Suspension Inspections',
      'Shock Replacement',
      'Strut Replacement',
      'Ball Joint Replacement',
      'Control Arm Replacement',
      'Tie Rod Replacement',
      'Sway Bar Link Replacement',
      'Wheel Bearing / Hub Assembly Replacement',
      'Steering Rack Replacement',
      'Power Steering Repair',
      'Power Steering Fluid Service',
    ],
  },
  {
    name: 'Tires & Wheels',
    icon: 'circle',
    blurb: 'Tire service and wheel work that keeps your tires wearing evenly.',
    services: [
      'Tire Replacement',
      'Tire Rotation',
      'Tire Repair',
      'Tire Pressure Inspection',
      'TPMS Diagnostics & Sensor Replacement',
      'Wheel Balancing',
      'Wheel & Tire Inspections',
    ],
  },
  {
    name: 'Heating & Air Conditioning',
    icon: 'thermometer',
    blurb:
      'Climate control diagnosis and repair, including both modern and traditional refrigerant systems.',
    services: [
      'A/C System Diagnostics',
      'A/C Performance Testing',
      'R-1234yf A/C Service - Newer Refrigerant Systems',
      'R-134a A/C Service - Traditional Refrigerant Systems',
      'A/C Refrigerant Recovery, Evacuation & Recharge',
      'A/C Leak Detection',
      'A/C Compressor Replacement',
      'A/C Condenser Replacement',
      'A/C Evaporator Replacement',
      'A/C Hose & Line Repair',
      'Blower Motor Replacement',
      'Heater Core Replacement',
      'Heating System Diagnostics & Repair',
    ],
  },
  {
    name: 'Engine Repair',
    icon: 'cog',
    blurb:
      'Performance concerns, leaks, and engine work handled by factory-trained technicians.',
    services: [
      'Engine Diagnostics',
      'Engine Performance & Drivability Repair',
      'Engine Tune-Ups',
      'Valve Cover Gasket Replacement',
      'Intake Manifold Gasket Replacement',
      'Oil Pan Gasket Replacement',
      'Timing Belt Replacement',
      'Timing Chain Repair',
      'Cylinder Head Repair',
      'Engine Replacement',
      'Engine Mount Replacement',
    ],
  },
  {
    name: 'Cooling System',
    icon: 'snowflake',
    blurb:
      'Overheating, leaks, and coolant service traced back to the source.',
    services: [
      'Cooling System Diagnostics',
      'Coolant System Pressure Testing',
      'Coolant / Antifreeze Service',
      'Coolant Flush',
      'Radiator Replacement',
      'Water Pump Replacement',
      'Thermostat Replacement',
      'Cooling Fan Repair & Replacement',
      'Coolant Hose Replacement',
      'Coolant Leak Diagnosis & Repair',
      'Overheating Diagnostics & Repair',
      'Heater Hose Replacement',
      'Coolant Reservoir Replacement',
    ],
  },
  {
    name: 'Electrical System',
    icon: 'zap',
    blurb:
      'Batteries, charging, wiring, and the intermittent faults that are hard to pin down.',
    services: [
      'Battery Testing & Replacement',
      'Alternator Testing & Replacement',
      'Starter Testing & Replacement',
      'Electrical System Diagnostics',
      'Wiring Repair',
      'Fuse & Relay Diagnosis',
      'Lighting Repair',
      'Power Window Repair',
      'Power Door Lock Repair',
    ],
  },
  {
    name: 'Fuel & Ignition Systems',
    icon: 'flame',
    blurb:
      'Fuel delivery and ignition work for hard starts, misfires, and rough running.',
    services: [
      'Fuel System Diagnostics',
      'Fuel Pump Replacement',
      'Fuel Injector Service & Replacement',
      'Ignition Coil Replacement',
      'Spark Plug Replacement',
      'Fuel Pressure Testing',
      'Throttle Body Service & Replacement',
    ],
  },
  {
    name: 'Transmission & Drivetrain',
    icon: 'settings2',
    blurb:
      'Shifting concerns, axles, and drivetrain repair explained before work begins.',
    services: [
      'Transmission Diagnostics',
      'Transmission Fluid Service',
      'Transmission Replacement',
      'Clutch Repair & Replacement',
      'CV Axle Replacement',
      'Driveshaft Repair',
      'Differential Service & Repair',
      'Transfer Case Service & Repair',
      'Drivetrain Diagnostics',
    ],
  },
  {
    name: 'Exhaust & Emissions',
    icon: 'wind',
    blurb: 'Exhaust noise, leaks, and emissions-related faults from the manifold back.',
    services: [
      'Exhaust System Inspection',
      'Exhaust Leak Repair',
      'Muffler Replacement',
      'Exhaust Pipe Repair',
      'Catalytic Converter Replacement',
      'Oxygen Sensor Replacement',
      'EVAP System Diagnostics & Repair',
      'Emissions System Diagnostics',
    ],
  },
  {
    name: 'Additional Services',
    icon: 'wrench',
    blurb:
      'The everyday work and inspections that keep a vehicle or a small fleet dependable.',
    services: [
      'Headlight & Exterior Lighting Replacement',
      'Serpentine Belt Replacement',
      'Windshield Wiper Replacement',
      'Vehicle Maintenance Consultations',
      'Fleet Vehicle Maintenance & Repair',
      'Used Vehicle Inspections',
      'Road-Trip / Pre-Trip Inspections',
    ],
  },
]

export const serviceCategories: ServiceCategory[] = rawCategories.map((c) => ({
  name: c.name,
  slug: slugify(c.name),
  icon: c.icon,
  blurb: c.blurb,
  services: c.services.map((s) => ({ name: s, slug: slugify(s) })),
}))

export function findCategory(slug?: string) {
  return serviceCategories.find((c) => c.slug === slug)
}

export function findService(categorySlug?: string, serviceSlug?: string) {
  const category = findCategory(categorySlug)
  if (!category) return undefined
  const service = category.services.find((s) => s.slug === serviceSlug)
  if (!service) return undefined
  return { category, service }
}
